<script lang="ts">
    import Card from "./components/Card.svelte";
    import {
        cType,
        ForecastType,
        organizationLocation,
        OrganizationLocation,
        PaletteType,
        Widget,
        widgetInfo,
        WidgetType,
    } from "./store";
    import {
        getConditionsForWidget,
        getCustomerStatus,
        getForecastForWidget,
        getLightningDelay,
        getOrgLocationForWidget,
        getWidgetInfo,
        getAqiForWidget
    } from "./api.svelte";
    import { parseBuilderMessage } from "./WidgetBuilderApi";
    import GlobalStyles from "./styles/GlobalStyles.svelte";
    import ErrorScreen from "./components/ErrorScreen.svelte";
    import LoadingScreen from "./components/LoadingScreen.svelte";
    import { LIGHTNING_REFRESH_MS, WIDGET_REFRESH_MS, shouldPollLightning } from "./refresh";
    import { onDestroy } from "svelte";

    export let widgetId: string;
    export let host: string;

    let widget: Widget;
    widgetInfo.subscribe((value: Widget) => {
        if (value) {
            widget = value;
        }
    });

    let orgLoc: OrganizationLocation;
    organizationLocation.subscribe((value: OrganizationLocation) => {
        if (value) {
            if (document.title != value.name) {
                document.title = value.name;
            }
            orgLoc = value;
        }
    });

    $: outerWidth = 0;

    const getWidgetData = async (widgetId: string) => {
        if (widgetId && widgetId != "builder") {
            // const parentUrl = window.parent.location; cant do this because of CORS
            // console.log('parentUrl', parentUrl.host)
            if (!host) {
                host = "direct";
            }

            let promises: Promise<void>[] = [];
            await getWidgetInfo(widgetId, host); // pass host to getWidgetInfo, do something on the backend
            promises.push(getOrgLocationForWidget(widget.orgLocId));
            getConditionsForWidget(widget.orgLocId); //this call usually takes > 1sec, dont want it to block showing the widget
            promises.push(getCustomerStatus(widgetId));
            promises.push(getLightningDelay(widgetId));

            // Get AQI for widget so if we add any AQI conditions we immediately have the data
            promises.push(getAqiForWidget(widget.orgLocId));

            if (
                widget.widgetForecasts &&
                widget.widgetForecasts.filter(
                    (f) => f.forecastType === ForecastType.DAILY,
                ).length > 0
            ) {
                promises.push(
                    getForecastForWidget(widget.orgLocId, ForecastType.DAILY),
                );
            }
            if (
                widget.widgetForecasts &&
                widget.widgetForecasts.filter(
                    (f) => f.forecastType === ForecastType.HOURLY,
                ).length > 0
            ) {
                promises.push(
                    getForecastForWidget(widget.orgLocId, ForecastType.HOURLY),
                );
            }

            return Promise.all(promises);
        }
        if (widgetId && widgetId === "builder") {
            const builderWidget: Widget = {
              id: "builder",
              theme: PaletteType.DARK,
              type: WidgetType.DEF,
              orgLocId: "builder",
              widgetConditions: [],
              primaryCondition: cType.TEMP,
              widgetForecasts: [],
              weatherStationId: "builder",
              primaryThreshold: 200,
              primaryPolicyTriggerId: undefined
            };
            widgetInfo.set(builderWidget);
        }
    };

    // Lightning polls on its own cadence (see refresh.ts). Everything else stays on WIDGET_REFRESH_MS.
    const refreshLightning = () => {
        if (shouldPollLightning(widgetId, document.visibilityState)) getLightningDelay(widgetId);
    };
    const widgetInterval = setInterval(() => getWidgetData(widgetId), WIDGET_REFRESH_MS);
    const lightningInterval = setInterval(refreshLightning, LIGHTNING_REFRESH_MS);
    const onVisibilityChange = () => {
        if (document.visibilityState === "visible") refreshLightning();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const stopPolling = () => {
        clearInterval(widgetInterval);
        clearInterval(lightningInterval);
        document.removeEventListener("visibilitychange", onVisibilityChange);
    };

    const onBuilderMessage = (event: MessageEvent) => {
        // Live boards ignore builder posts; only the builder preview yields the page to them.
        if (widgetId !== "builder") return;
        if (!event?.data || typeof event.data !== "string") return;

        const indexOfMessage = event.data.indexOf("message");
        if (indexOfMessage < 0) return;

        const message = event.data.substring(indexOfMessage + 8);
        if (!message) return;

        try {
            JSON.parse(message);
        } catch {
            return;
        }

        stopPolling(); // builder preview owns the stores for this page
        parseBuilderMessage(message);
    };
    window.addEventListener("message", onBuilderMessage);

    onDestroy(() => {
        stopPolling();
        window.removeEventListener("message", onBuilderMessage);
    });

    $: widgetPromise = getWidgetData(widgetId);
</script>

<GlobalStyles />
<svelte:window bind:outerWidth />

{#await widgetPromise}
    <LoadingScreen />
{:then}
    <Card />
{:catch error}
    <ErrorScreen {error} />
{/await}
